import { useState } from 'react'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { Checkbox } from '@/components/ui/checkbox'
import { fetchAssortmentPlanIds } from '@/api/assortmentPlans'
import { useAssortmentPlanSelectionStore } from '@/stores/useAssortmentPlanSelectionStore'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import type { AssortmentPlan, AssortmentPlansFilters } from '@/types'

export function PlanSelectionCell({
  data,
}: CustomCellRendererProps<AssortmentPlan>) {
  const { selectedPlanIds, selectPlans } = useAssortmentPlanSelectionStore()
  return (
    data && (
      <div
        className="flex h-full items-center justify-center"
        onClick={event => event.stopPropagation()}
      >
        <Checkbox
          aria-label={`Select ${data.name}`}
          checked={selectedPlanIds.includes(data.id)}
          onCheckedChange={checked => selectPlans([data.id], checked)}
          className="peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2"
        />
      </div>
    )
  )
}

export interface PlanSelectionHeaderProps {
  /** Total plans matching the current filters (all pages) */
  totalCount: number
  filters: Omit<AssortmentPlansFilters, 'page' | 'pageSize'>
}

/** Selects every plan matching the current filters, across all pages. */
export function PlanSelectionHeader({
  totalCount,
  filters,
}: PlanSelectionHeaderProps) {
  const { selectedPlanIds, selectPlans, clearSelection } =
    useAssortmentPlanSelectionStore()
  const [isLoading, setIsLoading] = useState(false)
  const count = selectedPlanIds.length
  const allSelected = totalCount > 0 && count >= totalCount

  const handleChange = async (checked: boolean) => {
    if (isLoading) return
    if (!checked) {
      clearSelection()
      return
    }
    // Shown checked right away; the ids arrive a moment later
    setIsLoading(true)
    try {
      selectPlans(await fetchAssortmentPlanIds(filters), true)
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to select plans'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Checkbox
      aria-label="Select all filtered plans"
      title="Select all filtered plans across all pages"
      checked={isLoading || allSelected}
      indeterminate={!isLoading && count > 0 && !allSelected}
      aria-busy={isLoading}
      disabled={!totalCount}
      onCheckedChange={checked => void handleChange(checked)}
      className="peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2"
    />
  )
}
